/**
 * Backend often prepends the API host onto an absolute S3 URL, e.g.
 * https://carsapi.saldr.tech/https://cars-saldr-tech.s3.amazonaws.com/...
 * or the broken single-slash form:
 * https://carsapi.saldr.tech/https:/cars-saldr-tech.s3.amazonaws.com/...
 * Always prefer the Amazon URL when present.
 */
export function sanitizeImageUrl(url) {
  if (url == null) return "";
  if (typeof url !== "string") {
    if (typeof url?.image === "string") return sanitizeImageUrl(url.image);
    if (typeof url?.url === "string") return sanitizeImageUrl(url.url);
    return "";
  }

  let trimmed = url.trim();
  if (!trimmed) return "";

  // Decode only when the scheme/host itself is percent-encoded.
  // Signed S3 query strings contain %2F/%3D in Signature and must stay intact.
  try {
    if (/^https?%3a/i.test(trimmed) || /https?%3a\/\//i.test(trimmed)) {
      trimmed = decodeURIComponent(trimmed);
    }
  } catch (error) {
    // keep original
  }

  if (trimmed.startsWith("/http://") || trimmed.startsWith("/https://")) {
    trimmed = trimmed.slice(1);
  }
  if (trimmed.startsWith("/http:/") || trimmed.startsWith("/https:/")) {
    trimmed = trimmed.slice(1);
  }

  // Normalize broken schemes: https:/host → https://host
  trimmed = trimmed.replace(/https:\/(?!\/)/gi, "https://");
  trimmed = trimmed.replace(/http:\/(?!\/)/gi, "http://");

  // Split accidentally concatenated absolute URLs:
  // https://a.com/1.jpghttps://bucket.s3.amazonaws.com/2.jpg
  const absoluteUrls = trimmed
    .split(/(?=https?:\/\/)/i)
    .map((part) => part.trim())
    .filter((part) => /^https?:\/\//i.test(part));
  if (absoluteUrls.length > 1) {
    const amazon = absoluteUrls.find((u) => /amazonaws\.com/i.test(u));
    trimmed = amazon || absoluteUrls[absoluteUrls.length - 1];
  } else if (absoluteUrls.length === 1) {
    trimmed = absoluteUrls[0];
  }

  // Nested absolute right after API host:
  // https://carsapi…/https://bucket.s3.amazonaws.com/...
  const nestedAfterHost = trimmed.match(
    /^https?:\/\/[^/]+\/(https?:\/\/.+)$/i
  );
  if (nestedAfterHost) {
    trimmed = nestedAfterHost[1];
  } else {
    const nestedAnywhere = trimmed.match(
      /https?:\/\/[^/\s]+\/(?:[^\s"'<>\\]*?)(https?:\/\/[^\s"'<>\\]+)/i
    );
    if (nestedAnywhere) {
      trimmed = nestedAnywhere[1];
    }
  }

  // Prefer an embedded Amazon S3 URL if the string still contains one
  const amazonMatch = trimmed.match(
    /https?:\/\/[^\s"'<>\\]*amazonaws\.com[^\s"'<>\\]*/i
  );
  if (amazonMatch) {
    return amazonMatch[0].replace(/[),.;]+$/g, "");
  }

  // Final scheme cleanup
  trimmed = trimmed.replace(/^http:\/(?!\/)/i, "http://");
  trimmed = trimmed.replace(/^https:\/(?!\/)/i, "https://");

  return trimmed.replace(/[),.;]+$/g, "");
}

/**
 * Resolve a usable vehicle image URL, or return fallback when missing/invalid.
 */
export function resolveVehicleImageUrl(item, fallback = "") {
  if (!item) return fallback;

  const candidates = [
    item?.main_image,
    Array.isArray(item?.images) ? item.images[0] : null,
    item?.product_image,
    item?.image,
  ];

  for (const candidate of candidates) {
    const clean = sanitizeImageUrl(candidate);
    if (clean && !/notavailable\.jpg/i.test(clean)) {
      return clean;
    }
  }

  return fallback;
}
